import {
  BOTTOM,
  CONTENT_WIDTH,
  DARK,
  GRAY,
  LIGHT,
  PAGE,
  attendanceKinds,
  attendanceTone,
  cardTable,
  condensed,
  courseCell,
  drawCourseCell,
  drawLogo,
  fullName,
  gradeText,
  gradeTone,
  kpi,
  number,
  periodAttendance,
  periodComment,
  periodGpa,
  periodGrade,
  periodHead,
  pill,
  sectionGap,
  shortPeriodName,
  tint,
} from './shared'

const TOP = 172

const header = (doc, ctx) => {
  const { margin, width } = PAGE
  if (ctx.logo) {
    const height = 40
    drawLogo(doc, ctx.logo, margin, 32, Math.min((ctx.logo.width / ctx.logo.height) * height, 170), height)
  }
  doc.setTextColor(...DARK)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text(ctx.schoolName ?? '', width - margin, 46, { align: 'right' })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9.5)
  doc.setTextColor(...GRAY)
  doc.text(ctx.subtitle, width - margin, 62, { align: 'right' })
  doc.setDrawColor(...ctx.primary)
  doc.setLineWidth(2)
  doc.line(margin, 86, width - margin, 86)
}

const banner = (doc, card, ctx) => {
  const { t, primary } = ctx
  const { student, gpa } = card
  const top = 100
  const height = 52
  const { margin } = PAGE

  doc.setFillColor(...tint(primary, 0.93))
  doc.roundedRect(margin, top, CONTENT_WIDTH, height, 8, 8, 'F')

  const textX = margin + 16
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...DARK)
  doc.setFontSize(13)
  doc.text(fullName(student), textX, top + 19)

  let fieldX = textX
  ctx.fields.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.setTextColor(...GRAY)
    doc.text(label.toUpperCase(), fieldX, top + 32)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8.5)
    doc.setTextColor(...DARK)
    doc.text(String(value), fieldX, top + 43)
    fieldX += Math.max(doc.getTextWidth(String(value)), 44) + 22
  })

  const tileWidth = 64
  const tileY = top + (height - 28) / 2
  const right = margin + CONTENT_WIDTH - 16
  kpi(doc, {
    x: right - tileWidth,
    y: tileY,
    width: tileWidth,
    label: t('Attendance'),
    value: ctx.attendanceRate == null ? '—' : `${ctx.attendanceRate}%`,
    tone: attendanceTone(ctx.attendanceRate),
  })
  if (gpa?.cumulative != null) {
    kpi(doc, {
      x: right - tileWidth * 2 - 8,
      y: tileY,
      width: tileWidth,
      label: t('Cumulative GPA'),
      value: number(gpa.cumulative),
      tone: primary,
      filled: true,
    })
  }
}

const sectionTitle = (doc, title, y, primary) => {
  doc.setFillColor(...primary)
  doc.rect(PAGE.margin, y - 8, 3, 11, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10.5)
  doc.setTextColor(...DARK)
  doc.text(title.toUpperCase(), PAGE.margin + 9, y + 1)
  return y + 10
}

export default {
  limit: BOTTOM,
  render(doc, card, ctx, density) {
    const { t, primary, periods, selected } = ctx
    const d = condensed(density)
    const look = { headFill: tint(primary, 0.9), headText: primary, outline: tint(primary, 0.82), divider: LIGHT }
    header(doc, ctx)
    banner(doc, card, ctx)

    const columns = periods.length + 1
    const body = []
    ;(card.courses ?? []).forEach(course => {
      body.push([
        courseCell(course, d),
        ...periods.map(period => ({ content: periodGrade(course, period), kind: 'grade' })),
        { content: gradeText(course.finalGrade) ?? '—', kind: 'grade', final: true },
      ])
      const comment = d.comments && periodComment(course, selected)
      if (comment) {
        body.push([
          {
            content: `“${comment}”`,
            colSpan: columns + 1,
            kind: 'comment',
            styles: { fontStyle: 'italic', fontSize: d.commentFont, textColor: GRAY, cellPadding: { top: 0, bottom: d.pad + 2, left: 20, right: 10 } },
          },
        ])
      }
    })

    let y = sectionTitle(doc, t('Grades'), TOP, primary)
    y = cardTable(doc, d, look, {
      startY: y,
      top: TOP,
      head: [[t('Course'), ...periods.map(period => periodHead(period.name)), periodHead(t('Final'))]],
      body: body.length ? body : [[{ content: t('No courses'), colSpan: columns + 1, styles: { textColor: GRAY } }]],
      columnStyles: Object.fromEntries(Array.from({ length: columns }, (_, i) => [i + 1, { halign: 'center', valign: 'middle', cellWidth: 50 }])),
      skipDivider: data => data.table.body[data.row.index + 1]?.raw?.[0]?.kind === 'comment',
      willDrawCell: data => {
        const kind = data.cell.raw?.kind
        if (data.section === 'body' && (kind === 'course' || (kind === 'grade' && data.cell.raw.content !== '—'))) data.cell.text = []
      },
      didDrawCell: data => {
        if (data.section !== 'body') return
        const { cell } = data
        const kind = cell.raw?.kind
        if (kind === 'course') drawCourseCell(doc, cell, d)
        if (kind === 'grade' && cell.raw.content !== '—') {
          pill(doc, String(cell.raw.content), cell.x + cell.width / 2, cell.y + cell.height / 2, cell.raw.final ? primary : gradeTone(cell.raw.content), d.pill)
        }
        if (kind === 'comment') {
          doc.setDrawColor(...tint(primary, 0.5))
          doc.setLineWidth(1.5)
          doc.line(cell.x + 12, cell.y + 1, cell.x + 12, cell.y + cell.height - d.pad - 1)
        }
      },
    })

    if (ctx.hasGpa) {
      y = sectionTitle(doc, t('GPA'), y + sectionGap(d), primary)
      const tiles = [
        ...periods.map(period => ({ label: shortPeriodName(period.name), value: number(periodGpa(card.gpa, period)) })),
        { label: t('Cumulative'), value: number(card.gpa?.cumulative), filled: true },
      ]
      tiles.forEach((tile, i) => kpi(doc, { x: PAGE.margin + i * 68, y: y + 2, width: 58, tone: primary, ...tile }))
      y += 30
    }

    y = sectionTitle(doc, t('Attendance'), y + sectionGap(d), primary)
    const kinds = attendanceKinds(t)
    kinds.forEach((kind, i) =>
      kpi(doc, { x: PAGE.margin + i * 74, y: y + 2, width: 64, label: kind.label, value: ctx.attendance.total?.[kind.key] ?? '—', tone: kind.tone }),
    )
    return cardTable(doc, d, look, {
      startY: y + 38,
      top: TOP,
      head: [[t('Marking Period'), ...kinds.map(kind => ({ content: kind.label, styles: { halign: 'center' } }))]],
      body: periods.map(period => {
        const row = periodAttendance(ctx.attendance, period)
        return [period.name, ...kinds.map(kind => ({ content: row[kind.key] ?? '—', styles: { halign: 'center' } }))]
      }),
    })
  },
}
